import React, { useState } from 'react';
import { PlanningCategory } from '../../types';
import { createPlanningCategory, updatePlanningCategory, deletePlanningCategory, seedDefaultCategories } from '../../services/planningService';
import { Plus, Trash2, Edit2, X, Check, Tag, Wand2 } from 'lucide-react';

interface ConceptCategoriesManagerProps {
  categories: PlanningCategory[];
  userId: string;
}

export const ConceptCategoriesManager: React.FC<ConceptCategoriesManagerProps> = ({ categories, userId }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [seeding, setSeeding] = useState(false);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      await seedDefaultCategories(userId);
    } finally {
      setSeeding(false);
    }
  };

  const handleOpenForm = (cat?: PlanningCategory) => {
    if (cat) {
      setEditingId(cat.id);
      setName(cat.name);
    } else {
      setEditingId(null);
      setName('');
    }
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingId(null);
    setName('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    if (editingId) {
      await updatePlanningCategory(userId, editingId, { name: name.trim() });
    } else {
      await createPlanningCategory(userId, { name: name.trim(), userId });
    }
    handleCloseForm();
  };

  const handleDelete = async (id: string) => {
    if (deletingId === id) {
      await deletePlanningCategory(userId, id);
      setDeletingId(null);
    } else {
      setDeletingId(id);
    }
  };

  return (
    <div className="bg-zinc-800/20 border border-zinc-700/50 rounded-2xl p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Tag size={18} className="text-indigo-400" />
          <h3 className="font-bold text-white tracking-tight">Gestionar Categorías</h3>
        </div>
        {!showForm && (
          <button
            onClick={() => handleOpenForm()}
            className="text-[10px] font-bold text-indigo-400 hover:text-indigo-300 uppercase tracking-widest flex items-center gap-1"
          >
            <Plus size={14} /> Agregar Categoría
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="flex items-center gap-2 mb-4 animate-in fade-in slide-in-from-top-2 duration-200">
          <input
            autoFocus
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Nombre de la categoría..."
            className="flex-1 bg-zinc-900 border border-zinc-700 rounded-xl px-4 py-2 text-sm text-white outline-none focus:border-indigo-500 transition-colors"
          />
          <button
            type="submit"
            className="p-2 bg-indigo-500 hover:bg-indigo-600 text-black rounded-lg transition-colors"
          >
            <Check size={18} />
          </button>
          <button
            type="button"
            onClick={handleCloseForm}
            className="p-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-400 rounded-lg transition-colors"
          >
            <X size={18} />
          </button>
        </form>
      )}

      <div className="flex flex-wrap gap-2">
        {categories.map((cat) => (
          <div
            key={cat.id}
            className="group flex items-center gap-2 bg-zinc-900 border border-zinc-800 px-3 py-1.5 rounded-xl transition-all hover:border-zinc-700"
          >
            <span className="text-sm text-zinc-300">{cat.name}</span>
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity ml-2">
              <button
                onClick={() => handleOpenForm(cat)}
                className="p-1 text-zinc-500 hover:text-indigo-400 transition-colors"
              >
                <Edit2 size={12} />
              </button>
              <button
                onClick={() => handleDelete(cat.id)}
                className={`p-1 transition-colors ${deletingId === cat.id ? 'text-red-500 animate-pulse' : 'text-zinc-500 hover:text-red-400'}`}
              >
                <Trash2 size={12} />
              </button>
            </div>
          </div>
        ))}
        {categories.length === 0 && !showForm && (
          <div className="flex flex-col items-center justify-center p-4 border-2 border-dashed border-zinc-800 rounded-2xl w-full">
            <p className="text-sm text-zinc-500 italic mb-4">No hay categorías personalizadas definidas.</p>
            <button
              onClick={handleSeed}
              disabled={seeding}
              className="flex items-center gap-2 bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-400 px-4 py-2 rounded-xl text-xs font-bold transition-all border border-indigo-500/20"
            >
              <Wand2 size={14} /> {seeding ? 'Cargando...' : 'Cargar categorías por defecto'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
